import { useMemo } from 'react';

import classNames from 'classnames';

import { WalletType } from '@api';

import EvmDark from '@uikit/icons/EvmDark';
import Google from '@uikit/icons/Google';

import {
  evmImage,
  google,
  metamaskTransparent,
  okxTransparent,
  phantomTransparent,
  trustWallet,
  walletConnect,
} from '@shared/assets/images/scope/login';
import useGoogleOAuthClient from '@shared/common/components/AuthModal/useGoogleOAuthClient';
import { useUserAgentDetect } from '@shared/common/providers/UserAgentDetectProvider';
import { walletIconMappings } from '@shared/constants';
import displayErrorToast from '@shared/helpers/displayErrorToast';
import { WalletOption } from '@shared/helpers/wallet-options';
import evm from '@shared/helpers/web3/evm';
import getWalletActionStyles from '@shared/helpers/web3/getWalletActionStyles';
import { useFeatureToggles } from '@shared/hooks/useFeatureToggles';

export type WalletOptions = Record<WalletType | 'GoogleOAuth' | 'Passkey', WalletOption>;

const commonStyles = classNames(
  'bg-no-repeat bg-none sm:bg-[right_3%_center] bg-[length:0px,_cover] sm:bg-[length:32px,_cover]',
  'disabled:cursor-not-allowed disabled:opacity-30', // [disabled]
  'hover:opacity-[90%]',
);

export const useWalletOptions = () => {
  const { isDesktop } = useUserAgentDetect();
  const client = useGoogleOAuthClient();
  const featureToggles = useFeatureToggles();
  const walletOptions: WalletOptions = useMemo(() => {
    return {
      EVM: {
        hide: false,
        name: 'EVM Wallet',
        style: { backgroundImage: `url('${evmImage.src}')` },
        className: classNames(commonStyles, 'bg-neutral-600'),
        Icon: EvmDark,
      },
      Passkey: {
        hide: !featureToggles.passkeyAuth,
        name: 'Passkey',
        className: classNames(commonStyles, 'bg-neutral-600'),
        Icon: walletIconMappings.Passkey,
      },
      SOLANA: {
        hide: false,
        name: 'Solana',
        className: classNames(commonStyles, 'bg-neutral-600'),
        Icon: walletIconMappings.SOLANA,
      },
      Metamask: {
        hide: !featureToggles.metamaskWallet,
        name: 'Metamask',
        style: {
          backgroundImage: `url('${metamaskTransparent.src}'), ${
            getWalletActionStyles('Metamask').background
          }`,
        },
        className: commonStyles,
        Icon: walletIconMappings.Metamask,
      },
      Trust: {
        hide: !featureToggles.trustWallet || !isDesktop,
        name: 'Trust',
        style: {
          backgroundImage: `url('${trustWallet.src}'), ${
            getWalletActionStyles('Trust').background
          }`,
        },
        className: commonStyles,
        Icon: walletIconMappings.Trust,
      },
      OKX: {
        hide: !featureToggles.okxWallet || !isDesktop,
        name: 'OKX',
        style: {
          backgroundImage: `url('${okxTransparent.src}'), ${
            getWalletActionStyles('OKX').background
          }`,
        },
        className: commonStyles,
        Icon: walletIconMappings.OKX,
        beginnerGuide: {
          beginUrl: 'http://okx.com/join/38094504',
          checkIsReady: () => (isDesktop ? evm.isOKXWalletExtensionActive() : false),
        },
      },
      Phantom: {
        hide: !featureToggles.displayPhantomWallet || !isDesktop,
        name: 'Phantom',
        style: {
          backgroundImage: `url('${phantomTransparent.src}'), ${
            getWalletActionStyles('Phantom').background
          }`,
        },
        className: commonStyles,
        Icon: walletIconMappings.Phantom,
      },
      WalletConnect: {
        hide: !featureToggles.walletConnect,
        name: 'Wallet Connect',
        style: {
          backgroundImage: `url('${walletConnect.src}'), ${
            getWalletActionStyles('WalletConnect').background
          }`,
        },
        className: commonStyles,
        Icon: walletIconMappings.WalletConnect,
      },
      WalletConnect2: {
        hide: true,
        name: 'Wallet Connect 2',
        style: {
          backgroundImage: `url('${walletConnect.src}'), ${
            getWalletActionStyles('WalletConnect2').background
          }`,
        },
        className: commonStyles,
        Icon: walletIconMappings.WalletConnect,
      },
      GoogleOAuth: {
        hide: !featureToggles['oauth2.0'] || !client,
        name: 'Google',
        style: {
          backgroundImage: `url('${google.src}')`,
        },
        className: classNames(
          commonStyles,
          'bg-neutral-50 text-neutral-600 border border-primary-600',
        ),
        clickHandler: () => {
          try {
            client?.requestCode();
          } catch (error) {
            displayErrorToast({ error });
          }
        },
        Icon: Google,
      },
    };
  }, [featureToggles, isDesktop, client]);

  return walletOptions;
};
